import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircleIcon,
  CurrencyDollarIcon,
  ExclamationIcon,
  OfficeBuildingIcon,
  RefreshIcon,
  UserGroupIcon,
  ViewGridIcon,
} from "@heroicons/react/outline";
import { toast } from "react-toastify";
import { getProperties, getUsers } from "../../../apis/channelManager";
import InfoTooltip from "../../../components/InfoTooltip";

const listFrom = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const propertyRoomTypes = (property) => listFrom(property?.roomtypes || property?.room_types);
const propertyUsers = (property) => listFrom(property?.users);
const propertyKey = (property) => property?.resort_code || property?.property_code || property?.resort_id;

const AnalyticsPage = () => {
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadAnalytics = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    const [propertiesResult, usersResult] = await Promise.allSettled([getProperties(), getUsers()]);

    if (propertiesResult.status === "fulfilled") {
      const payload = propertiesResult.value;
      setProperties(listFrom(payload?.resorts || payload));
    } else {
      toast.error("Unable to load property analytics");
    }

    if (usersResult.status === "fulfilled") {
      const payload = usersResult.value;
      setUsers(listFrom(payload?.users || payload));
    }

    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const metrics = useMemo(() => {
    const roomTypes = properties.reduce((total, property) => total + propertyRoomTypes(property).length, 0);
    const nestedUsers = properties.flatMap(propertyUsers);
    const userIds = new Set([...users, ...nestedUsers].map((user) => user?.user_id || user?.id || user?.email).filter(Boolean));
    const configuredCurrencies = properties.filter((property) => property?.currency_code).length;
    const readyProperties = properties.filter((property) => property?.currency_code && propertyRoomTypes(property).length > 0).length;

    return {
      roomTypes,
      teamMembers: userIds.size,
      configuredCurrencies,
      readyProperties,
      readiness: properties.length ? Math.round((readyProperties / properties.length) * 100) : 0,
    };
  }, [properties, users]);

  const largestRoomCount = Math.max(...properties.map((property) => propertyRoomTypes(property).length), 1);

  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-slate-600 shadow-sm">Loading analytics…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-indigo-600">Operations overview</p>
          <div className="mt-1 flex items-center gap-2"><h1 className="text-3xl font-bold text-slate-900">Analytics</h1><InfoTooltip label="About analytics">Monitor configuration and operational coverage for the properties available to this account.</InfoTooltip></div>
        </div>
        <button type="button" onClick={() => loadAnalytics(true)} disabled={refreshing} className="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-bold text-indigo-700 shadow-sm hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60">
          <RefreshIcon className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh data"}
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric icon={<OfficeBuildingIcon className="h-6 w-6" />} label="Managed properties" value={properties.length} tone="indigo" />
        <Metric icon={<ViewGridIcon className="h-6 w-6" />} label="Configured room types" value={metrics.roomTypes} tone="emerald" />
        <Metric icon={<UserGroupIcon className="h-6 w-6" />} label="Team members" value={metrics.teamMembers} tone="amber" />
        <Metric icon={<CurrencyDollarIcon className="h-6 w-6" />} label="Currency coverage" value={`${metrics.configuredCurrencies}/${properties.length}`} tone="violet" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2"><h2 className="font-bold text-slate-900">Property footprint</h2><InfoTooltip label="About property footprint">This view compares room-type coverage and assigned users for each property.</InfoTooltip></div>
            </div>
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700">Live records</span>
          </div>
          {properties.length === 0 ? (
            <EmptyState text="No properties are available for this account yet." />
          ) : (
            <div className="space-y-4">
              {properties.map((property) => {
                const rooms = propertyRoomTypes(property).length;
                const assignedUsers = propertyUsers(property).length;
                const code = propertyKey(property);
                return (
                  <div key={code} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-800">{property.resort_name || "Unnamed property"}</p>
                        <p className="text-xs text-slate-500">{code || "No property code"} · {assignedUsers} assigned user{assignedUsers === 1 ? "" : "s"}</p>
                      </div>
                      <span className="whitespace-nowrap text-sm font-bold text-slate-700">{rooms} room type{rooms === 1 ? "" : "s"}</span>
                    </div>
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                      <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-400" style={{ width: `${Math.max((rooms / largestRoomCount) * 100, rooms ? 7 : 0)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5">
            <div className="flex items-center gap-2"><h2 className="font-bold text-slate-900">Configuration readiness</h2><InfoTooltip label="About configuration readiness">Readiness is calculated from the property records currently available, including currency, room types, and team access.</InfoTooltip></div>
          </div>
          <div className="mb-6 flex items-center gap-4 rounded-xl bg-slate-50 p-4">
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full" style={{ background: `conic-gradient(#4f46e5 ${metrics.readiness}%, #e2e8f0 0)` }}>
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-lg font-bold text-slate-900">{metrics.readiness}%</div>
            </div>
            <div><p className="font-bold text-slate-800">Ready for operations</p><p className="text-sm text-slate-500">{metrics.readyProperties} of {properties.length} properties have a currency and room types configured.</p></div>
          </div>
          <div className="space-y-3">
            <ReadinessRow icon={<CurrencyDollarIcon className="h-5 w-5" />} label="Currency configured" value={`${metrics.configuredCurrencies} of ${properties.length}`} complete={metrics.configuredCurrencies === properties.length && properties.length > 0} />
            <ReadinessRow icon={<ViewGridIcon className="h-5 w-5" />} label="Room types configured" value={`${properties.filter((property) => propertyRoomTypes(property).length > 0).length} of ${properties.length}`} complete={properties.length > 0 && properties.every((property) => propertyRoomTypes(property).length > 0)} />
            <ReadinessRow icon={<UserGroupIcon className="h-5 w-5" />} label="Team access" value={`${metrics.teamMembers} member${metrics.teamMembers === 1 ? "" : "s"}`} complete={metrics.teamMembers > 0} />
          </div>
        </section>
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800"><span className="font-semibold">Configuration coverage</span><InfoTooltip label="Analytics coverage">Analytics currently reports configuration data available through the Channel Manager API. Revenue, occupancy, and booking trends can be added once reservation and inventory aggregation endpoints are enabled for the account.</InfoTooltip></div>
    </div>
  );
};

const Metric = ({ icon, label, value, tone }) => {
  const tones = { indigo: "border-indigo-100 bg-indigo-50 text-indigo-600", emerald: "border-emerald-100 bg-emerald-50 text-emerald-600", amber: "border-amber-100 bg-amber-50 text-amber-600", violet: "border-violet-100 bg-violet-50 text-violet-600" };
  return <div className={`rounded-2xl border p-5 shadow-sm ${tones[tone]}`}><div className="mb-3">{icon}</div><p className="text-sm font-semibold text-slate-600">{label}</p><p className="mt-1 text-3xl font-bold text-slate-900">{value}</p></div>;
};

const ReadinessRow = ({ icon, label, value, complete }) => <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-3"><div className="flex items-center gap-3"><span className="text-slate-400">{icon}</span><span className="text-sm font-semibold text-slate-700">{label}</span></div><span className={`flex items-center gap-1 text-xs font-bold ${complete ? "text-emerald-600" : "text-amber-600"}`}>{complete ? <CheckCircleIcon className="h-4 w-4" /> : <ExclamationIcon className="h-4 w-4" />}{value}</span></div>;
const EmptyState = ({ text }) => <p className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">{text}</p>;

export default AnalyticsPage;
